/**
 * MobileCompactRow — the shared compact-density card shell (TV1-2294 ticket 23).
 *
 * ONE COMPACT LAYOUT FOR ALL THREE LISTS. Publications, inventory and orders
 * each keep their own comfortable card (they show genuinely different fields),
 * but the compact card is a single component with slots. That is deliberate:
 * the compact geometry is the part with a hard, measurable acceptance bar —
 * "no horizontal scroll and no clipped text at 320px in both densities" — and
 * verifying it once beats verifying three copies that will drift. This PR has
 * already had to delete five `BaseFilter` copies and reconcile two hand-written
 * account-menu lists; a fourth copy of a flex row is not worth the freedom.
 *
 * GEOMETRY, at the 320px worst case. Container padding leaves 304px, the card's
 * own 10px inline padding and 1px borders leave 282px for `.row`:
 *
 *   checkbox 20 + gap 8 + media 36 + gap 8 + [content] + gap 8 + actions 26
 *   = 106px of fixed furniture, so `.content` gets ~176px.
 *
 * Every flex item on that path carries `min-width: 0`, so an unbreakable token
 * (a long SKU, a deep ML category path, an e-mail) ellipsizes inside the content
 * column instead of widening the DOCUMENT. HEIGHT is 36 (media) + 16 (block
 * padding) + 2 (borders) = **54px**, against 183-223px for the comfortable
 * cards.
 *
 * SELECTED SURFACE — OWNER OVERRIDE, DO NOT ADD THE ACCENT BAR.
 * `.claude/rules/design-system.md` prescribes
 * `box-shadow: inset 3px 0 0 0 primary-5` for a selected list row; the ticket
 * owner rejected it ("remove the left border on selection. the background and
 * normal border is fine"), and all three comfortable cards already comply. The
 * compact card is the brand-teal fill + its normal border, nothing else.
 *
 * INTERACTION mirrors the comfortable cards exactly: the CHECKBOX selects, the
 * BODY opens, and the checkbox and the actions are SIBLINGS of the body (never
 * descendants) so a tap on either can never bubble into `onOpen`.
 *
 * BOTH HALVES OF THAT SENTENCE ARE OPTIONAL (ticket 45). `checkbox` and `onOpen`
 * are independent, and a surface may have neither: warehouses (ticket 42, the
 * first table surface to adopt this shell) has no selection column on desktop
 * AND no detail view to open. It had to pass `checkbox={null}` to satisfy a
 * required prop, and got a `cursor: pointer` body that does nothing. Both are
 * fixed here rather than 19 times over:
 *
 *   - `checkbox` is optional. Omitting it costs no layout: React renders
 *     nothing, and flex `gap` only applies BETWEEN rendered items, so `.row`
 *     collapses from three children to two with no stray 8px. Measured at 320px,
 *     the body's left edge moves 35px -> 9px (i.e. exactly the card's own 9px
 *     inline padding + border) and the 53.69px card height is unchanged.
 *   - `cursor: pointer` now follows `onOpen`. It lived unconditionally on
 *     `.body`; cosmetic on touch, but plainly wrong in a desktop-narrow window
 *     where a mouse pointer is real and nothing happens on click.
 */

import type { ReactNode } from 'react';
import { Paper, Text } from '@mantine/core';
import classes from './MobileCompactRow.module.css';

export type MobileCompactRowProps = {
  /** Brand-teal fill. The caller computes it from the domain's selectors. */
  'selected'?: boolean;
  /**
   * Tapping the body opens the row's detail view. OMIT IT on a surface with no
   * detail view — the body then carries no `cursor: pointer` either.
   */
  'onOpen'?: () => void;
  /**
   * The domain's own selection control, rendered as a sibling of the body. OMIT
   * IT on a list with no selection column (ticket 45): nothing is rendered and
   * no flex gap is left behind.
   */
  'checkbox'?: ReactNode;
  /**
   * Thumbnail / logo, dropped into a fixed 36x36 box. 36px is the ticket's
   * floor: the owner explicitly rejected 16px imagery, and the box is fixed so
   * a row with no picture is exactly as tall as one with a picture and the list
   * does not jog while scrolling. Pure ICONS inside stay 16-18px.
   */
  'media'?: ReactNode;
  /**
   * The row's primary line. Pass the plain string — this component owns the
   * clamping and the typography, so a caller cannot accidentally let it wrap to
   * two lines and break the fixed compact height.
   */
  'title': ReactNode;
  /** `title` attribute for the clamped line, so a truncated value stays readable. */
  'titleTooltip'?: string;
  /** One line of secondary metadata — badges and/or dimmed text. */
  'meta'?: ReactNode;
  /**
   * Trailing controls, typically `MobileRowActionsMenu`. Compact must not drop
   * capability, so this slot is where a collapsed action strip lands.
   */
  'actions'?: ReactNode;
  /**
   * Full-width slot under the row. Used only where the content is itself an
   * ACTION and therefore cannot be dropped as "secondary metadata" — inventory's
   * warehouse stock badges open the stock-update modal.
   */
  'below'?: ReactNode;
  /** Extra card class (e.g. inventory's conflicted border). */
  'className'?: string;
  'data-testid'?: string;
};

export const MobileCompactRow = ({
  selected = false,
  onOpen,
  checkbox,
  media,
  title,
  titleTooltip,
  meta,
  actions,
  below,
  className,
  'data-testid': testId
}: MobileCompactRowProps ) => (
  <Paper
    withBorder
    data-testid={testId}
    className={`${ classes.card }${ selected ? ` ${ classes.selected }` : '' }${ className ? ` ${ className }` : '' }`}
  >
    <div className={classes.row}>
      {checkbox}

      {/*
        `.bodyTappable` carries the `cursor: pointer`, so a body with no `onOpen`
        does not advertise an interaction it will not perform.
      */}
      <div
        className={`${ classes.body }${ onOpen ? ` ${ classes.bodyTappable }` : '' }`}
        onClick={onOpen}
      >
        {media !== undefined && <div className={classes.media}>{media}</div>}

        <div className={classes.content}>
          {/*
            `fz` / `fw` are Mantine PROPS, not CSS-module rules, on purpose:
            `Text`'s own root class sets `font-size: var(--mantine-font-size-md)`,
            and a single-class module rule would only win if this stylesheet
            happened to load after Mantine's. The props compile to inline custom
            properties, which always win. `.title` keeps just the colour and the
            `min-width: 0` the ellipsis depends on.
          */}
          <Text
            component="div"
            className={classes.title}
            fz={13}
            fw={600}
            lineClamp={1}
            title={titleTooltip}
          >
            {title}
          </Text>
          {meta !== undefined && <div className={classes.meta}>{meta}</div>}
        </div>
      </div>

      {actions !== undefined && <div className={classes.actions}>{actions}</div>}
    </div>

    {below !== undefined && <div className={classes.below}>{below}</div>}
  </Paper>
);

export default MobileCompactRow;
